<template>
  <div>
    <div id="slider" ref="slider"></div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';

export default Vue.extend({
  name: 'Slider',
  props: {
    max: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      required: true,
    },
    value: {
      type: [Array, Number],
      required: true,
    },
    step: {
      type: Number,
      default: 1,
    },
    tooltips: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
      default: '',
    },
    suffix: {
      type: String,
      default: '',
    },
    decimalPlace: {
      type: Number,
      default: 0,
    },
    margin: {
      type: Number,
      default: 1,
    },
  },
  data(): any {
    return {
      slider: null,
    };
  },
  computed: {
    stubbedClasses() {
      return [
        'noUi-target',
        'noUi-ltr',
        'noUi-horizontal',
        'noUi-vertical',
        'noUi-txt-dir-ltr',
        'noUi-base',
        'noUi-connect',
        'noUi-connects',
        'noUi-handle-upper',
        'noUi-handle',
        'noUi-handle.noUi-handle-lower',
        'noUi-touch-area',
        'noUi-origin',
        'noUi-state-tap',
        '[disabled]',
      ];
    },
  },
  mounted(): any {
    const element = this.$refs.slider;

    this.slider = noUiSlider.create(element, {
      start: this.value,
      connect: this.value.length > 1 ? true : 'lower',
      tooltips: this.tooltips,
      range: {
        min: this.min,
        max: this.max,
      },
      ariaFormat: wNumb({
        decimals: 0,
      }),
      step: this.step,
      format: wNumb({
        suffix: this.suffix,
        prefix: this.prefix,
        decimals: this.decimalPlace,
      }),
      margin: this.margin,
    });

    this.slider.target.querySelector('.noUi-handle').ariaLabel = 'Slider control';

    this.slider.on('update', (_0, _1, unencoded: any) => {
      // unencoded is the raw values passed without string formatting
      this.$emit('input', unencoded);
    });

    this.slider.on('end', () => {
      // remove focus on handle when dragged by mouse only
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    });
  },
  beforeDestroy() {
    this.slider.destroy();
  },
});
</script>

<style lang="scss">
.noUi-target,
.noUi-target * {
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-user-select: none;
  -ms-touch-action: none;
  touch-action: none;
  -ms-user-select: none;
  -moz-user-select: none;
  user-select: none;
  -moz-nox-sizing: border-box;
  box-sizing: border-box;
}
.noUi-target {
  position: relative;
  direction: ltr;
  background: theme('colors.grey.divider.contrast');
  border-radius: 4px;
}
.noUi-base,
.noUi-connects {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
}
.noUi-connects {
  overflow: hidden;
  z-index: 0;
  border-radius: 4px;
}
.noUi-connect,
.noUi-origin {
  will-change: transform;
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  transform-origin: 0 0;
  transform-style: flat;
  -ms-transform-origin: 0 0;
  -webkit-transform-origin: 0 0;
  -webkit-transform-style: preserve-3d;
}
.noUi-connect {
  height: 100%;
  width: 100%;
  background: theme('colors.primary.DEFAULT');
}
.noUi-origin {
  height: 10%;
  width: 10%;
}
html:not([dir='rtl']) .noUi-horizontal .noUi-origin {
  left: auto;
  right: 0;
}
.noUi-vertical .noUi-origin {
  width: 0;
}
.noUi-horizontal .noUi-origin {
  height: 0;
}
.noUi-handle {
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  position: absolute;
  border-radius: 100%;
  background: theme('colors.primary.DEFAULT');
  box-shadow: 0 2px 4px 0 rgba(5, 7, 15, 0.2);
  cursor: pointer;
  &:hover,
  &:focus-visible {
    background: theme('colors.brand.contrast');
  }
  &:focus-visible {
    outline: 1px dotted theme('colors.brand.contrast');
    outline-offset: 2px;
  }
}
.noUi-touch-area {
  height: 100%;
  width: 100%;
}
.noUi-state-tap .noUi-connect,
.noUi-state-tap .noUi-origin {
  -webkit-transition: transform 0.3s;
  transition: transform 0.3s;
}
.noUi-state-drag * {
  cursor: inherit !important;
}
.noUi-horizontal {
  height: 3px;
}
.noUi-horizontal .noUi-handle {
  border-radius: 100%;
  width: 24px;
  height: 24px;
  left: -14px;
  top: -10px;
}
.noUi-vertical {
  width: '18px';
}
.noUi-vertical .noUi-handle {
  border-radius: 100%;
  width: 32px;
  height: 32px;
  left: -6px;
  top: -17px;
}
html:not([dir='rtl']) .noUi-horizontal .noUi-handle {
  right: -17px;
  left: auto;
}
.noUi-draggable {
  cursor: ew-resize;
}
.noUi-vertical .noUi-draggable {
  cursor: ns-resize;
}
.noUi-active {
  background: theme('colors.brand.contrast');
}
[disabled].noUi-connect {
  background: #b8b8b8;
}
[disabled].noUi-target [disabled].noUi-handle,
[disabled].noUi-handle {
  cursor: not-allowed;
}
.noUi-pips,
.noUi-pips * {
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}
.noUi-pips {
  position: absolute;
  color: #999;
}
.noUi-value {
  position: absolute;
  white-space: nowrap;
  text-align: center;
}
.noUi-value-sub {
  color: #ccc;
  font-size: 10px;
}
.noUi-marker {
  position: absolute;
  background: #ccc;
}
.noUi-marker-sub {
  background: #aaa;
}
.noUi-marker-large {
  background: #aaa;
}
.noUi-pips-horizontal {
  padding: 10px 0;
  height: 80px;
  top: 100%;
  left: 0;
  width: 100%;
}
.noUi-value-horizontal {
  -webkit-transform: translate(-50%, 50%);
  transform: translate(-50%, 50%);
}
.noUi-rtl .noUi-value-horizontal {
  -webkit-transform: translate(50%, 50%);
  transform: translate(50%, 50%);
}
.noUi-marker-horizontal.noUi-marker {
  margin-left: -1px;
  width: 2px;
  height: 5px;
}
.noUi-marker-horizontal.noUi-marker-sub {
  height: 10px;
}
.noUi-marker-horizontal.noUi-marker-large {
  height: 15px;
}
.noUi-pips-vertical {
  padding: 0 10px;
  height: 100%;
  top: 0;
  left: 100%;
}
.noUi-value-vertical {
  -webkit-transform: translate(0, -50%);
  transform: translate(0, -50%);
  padding-left: 25px;
}
.noUi-rtl .noUi-value-vertical {
  -webkit-transform: 'translate(0, 50%)';
  transform: translate(0, 50%);
}
.noUi-marker-vertical.noUi-marker {
  width: 5px;
  height: 2px;
  margin-top: -1px;
}
.noUi-marker-vertical.noUi-marker-sub {
  width: 10px;
}
.noUi-marker-vertical.noUi-marker-large {
  width: 15px;
}
.noUi-tooltip {
  display: block;
  position: absolute;
  color: theme('colors.grey.body');
  padding: 0.5rem;
  text-align: center;
  white-space: nowrap;
}
.noUi-horizontal .noUi-tooltip {
  -webkit-transform: translate(-50%, 0);
  transform: translate(-50%, 0);
  left: 50%;
  bottom: 120%;
}
.noUi-vertical .noUi-tooltip {
  -webkit-transform: translate(0, -50%);
  transform: translate(0, -50%);
  top: 50%;
  right: 120%;
}
</style>
